// The controls of the customer modes' own content (Yaron, 2026-10-22): Maintenance's work (vegetation
// trimming / flat spots / roadside terrain), Existing projects / rehab's project segment, New project
// spawning's hotspot settings. The page applies them; these only show and change them.
import React from 'react';
import { MAINT_TASKS } from './modes';
import { OBSTRUCTION_LABELS } from './rules';

/** The kinds of Maintenance work, as cards: the first thing Maintenance asks. */
export function MaintenanceTasks({ task, onChoose }) {
  return (
    <div className="so-tasks">
      <p className="safety-muted so-tasks-q">What maintenance work are you planning?</p>
      <div className="so-tasks-grid">
        {MAINT_TASKS.map((t) => (
          <button key={t.key} type="button" className={`so-mode-card so-task-card ${task === t.key ? 'so-mode-last' : ''}`}
            onClick={() => onChoose(t.key)} title={`Open ${t.label}`}>
            <span className="so-mode-icon"><i className={`fa-solid ${t.icon}`} aria-hidden="true" /></span>
            <span className="so-mode-name">{t.label}</span>
            <span className="so-mode-blurb">{t.blurb}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** Above the filters in Maintenance: the work switch and what it narrows to. */
export function MaintenanceBar({ task, onTask }) {
  return (
    <div className="so-usecase-bar">
      <div className="so-mode-switch so-task-switch" role="group" aria-label="Maintenance work">
        {MAINT_TASKS.map((t) => (
          <button key={t.key} type="button" aria-pressed={task === t.key} onClick={() => onTask(t.key)}>
            <i className={`fa-solid ${t.icon}`} aria-hidden="true" /> {t.label}
          </button>
        ))}
      </div>
      {task === 'vegetation' && (
        <span className="so-lock-chip" title="Vegetation trimming keeps the SSD and ISD deficiencies whose sight line is blocked by vegetation">
          <i className="fa-solid fa-lock" aria-hidden="true" /> Obstruction: {OBSTRUCTION_LABELS.vegetation}
        </span>
      )}
      {task === 'flat' && (
        <span className="so-lock-chip" title="Flat spots keeps the FSA deficiencies: where the gradient lets water stand on the pavement">
          <i className="fa-solid fa-lock" aria-hidden="true" /> Analysis: FSA · flat spots
        </span>
      )}
      {task === 'roadside' && (
        <span className="so-lock-chip" title="Roadside terrain keeps the SSD and ISD deficiencies whose sight line is blocked by terrain">
          <i className="fa-solid fa-lock" aria-hidden="true" /> Obstruction: {OBSTRUCTION_LABELS.terrain}
        </span>
      )}
    </div>
  );
}

/** Existing projects / rehab: the one project segment the page shows. */
export function ProjectSegmentPicker({ segments, value, onChange, nameOf }) {
  return (
    <label className="safety-filter so-project-seg" htmlFor="so-project-seg"><span>Project segment</span>
      <select id="so-project-seg" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        {segments.map((s) => (
          <option key={s.key} value={s.key}>
            {nameOf(s)} · MP {(s.mpMin || 0).toFixed(1)}–{(s.mpMax || 0).toFixed(1)}{s.projectId ? ` · #${s.projectId}` : ''}
          </option>
        ))}
      </select>
    </label>
  );
}

/** New project spawning: how to find the candidates — the hotspot rule, or the best stretches of a project length. */
export function HotspotSettings({ value, onChange }) {
  const set = (patch) => onChange({ ...value, ...patch });
  const numIn = (v, lo, hi, step) => {
    const x = Number(v);
    return Number.isFinite(x) ? Math.min(Math.max(Math.round(x / step) * step, lo), hi) : null;
  };
  const byLength = value.findBy === 'length';
  // one row under its label: the way to find, its numbers, the directions
  return (
    <div className="safety-filter so-hotspot-settings"><span>Find by</span>
      <div className="so-hs-row">
        <span className="ov-seg" role="group" aria-label="Find by">
          <button type="button" aria-pressed={!byLength} onClick={() => set({ findBy: 'cluster' })}
            title="Stretches where deficiency sites follow each other closely">Hotspot rule</button>
          <button type="button" aria-pressed={byLength} onClick={() => set({ findBy: 'length' })}
            title="The best stretches of a given project length: what the budget covers">Project length</button>
        </span>
        {byLength ? (
          <span className="ov-inline" title="The best stretches of this length; a site counts by the part of it inside the stretch">
            <input type="number" className="so-num-input" aria-label="Project length (mi)" min={0.1} max={20} step={0.1} value={value.lengthMi}
              onChange={(e) => { const v = numIn(e.target.value, 0.1, 20, 0.05); if (v != null) set({ lengthMi: Number(v.toFixed(2)) }); }} /> mi
          </span>
        ) : (
          <span className="ov-inline" title="Sites at most this far apart join one hotspot; a hotspot needs at least this many sites">
            within
            <input type="number" className="so-num-input" aria-label="Hotspot gap (mi)" min={0.05} max={2} step={0.05} value={value.gapMi}
              onChange={(e) => { const v = numIn(e.target.value, 0.05, 2, 0.05); if (v != null) set({ gapMi: Number(v.toFixed(2)) }); }} /> mi,
            min
            <input type="number" className="so-num-input" aria-label="Hotspot minimum sites" min={2} max={50} step={1} value={value.minSites}
              onChange={(e) => { const v = numIn(e.target.value, 2, 50, 1); if (v != null) set({ minSites: v }); }} /> sites
          </span>
        )}
        <span className="ov-seg" role="group" aria-label="Hotspot directions">
          <button type="button" aria-pressed={!value.byDirection} onClick={() => set({ byDirection: false })}
            title="A hotspot joins the sites of both directions">Both directions</button>
          <button type="button" aria-pressed={value.byDirection} onClick={() => set({ byDirection: true })}
            title="Each direction has its own hotspots">Each direction</button>
        </span>
      </div>
    </div>
  );
}
